// components/workspace-timeline/WorkspaceTimelineLaneRow.tsx
import React, { useCallback, useMemo } from 'react';
import { Eye, EyeOff, GripVertical } from '@/components/ui/lucide-memo';
import { scissorsClosedCursor, scissorsCursor } from '#lib/svg';
import { WorkspaceTimelineTrackBlock } from './WorkspaceTimelineTrackBlock';
import {
  areResolvedTimelineTracksEqual,
  areTimelineRangePresentationsEqual,
  areTimelineToolStatesEqual,
  areTimelineTrackArraysEqual,
  signatureHasId,
} from './presentation';
import type {
  TimelineLaneLayoutPresentation,
  TimelineLaneRowActions,
  TimelineRangePresentation,
  TimelineToolState,
  TimelineTrackBlockActions,
  TimelineTrackBlockState,
  WorkspaceTimelineLanePresentationRow,
} from './types';

interface WorkspaceTimelineLaneRowProps {
  row: WorkspaceTimelineLanePresentationRow;
  range: TimelineRangePresentation;
  layout: TimelineLaneLayoutPresentation;
  tools: TimelineToolState;
  actions: TimelineLaneRowActions;
}

const TIMELINE_LANE_DROP_PREVIEW_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: '1px',
  bottom: '1px',
  borderRadius: '6px',
  border: '1px dashed rgba(236,236,236,0.45)',
  opacity: 0.85,
};
const TIMELINE_LANE_COMPACT_PREVIEW_OVERLAY_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: '0px',
  right: '0px',
  top: '0px',
  bottom: '0px',
  borderRadius: '6px',
  zIndex: 1,
};
const TIMELINE_LANE_COMPACT_PREVIEW_BLOCK_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: '1px',
  bottom: '1px',
  borderRadius: '6px',
  opacity: 0.92,
  zIndex: 2,
};
const TIMELINE_LANE_COMPACT_COUNT_STYLE: React.CSSProperties = {
  width: '20px',
  height: '18px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#AFAFAF',
  fontSize: 12,
  fontWeight: 500,
  opacity: 0.95,
  userSelect: 'none',
};
const TIMELINE_LANE_NAME_STYLE: React.CSSProperties = { fontSize: 'inherit' };
const areTimelineLanePresentationRowsEqual = (
  previous: WorkspaceTimelineLanePresentationRow,
  next: WorkspaceTimelineLanePresentationRow
): boolean => (
  areResolvedTimelineTracksEqual(previous.primaryTrack, next.primaryTrack) &&
  areTimelineTrackArraysEqual(previous.laneTracks, next.laneTracks) &&
  previous.previewLayerId === next.previewLayerId &&
  previous.timelineDropPreviewOffsetPercent === next.timelineDropPreviewOffsetPercent &&
  previous.timelineDropPreviewWidthPercent === next.timelineDropPreviewWidthPercent &&
  previous.timelineDropPreviewColor === next.timelineDropPreviewColor &&
  previous.isTrackLaneCompactPreviewTrack === next.isTrackLaneCompactPreviewTrack &&
  previous.trackLanePreviewOffsetPercent === next.trackLanePreviewOffsetPercent &&
  previous.trackLanePreviewWidthPercent === next.trackLanePreviewWidthPercent &&
  previous.hasTrackLanePreviewConflict === next.hasTrackLanePreviewConflict &&
  previous.activeTrackId === next.activeTrackId &&
  previous.activeDragTrackId === next.activeDragTrackId &&
  previous.activeTrackOrderDragId === next.activeTrackOrderDragId &&
  previous.selectedLayerIdsSignature === next.selectedLayerIdsSignature &&
  previous.separatingSoundTrackIdsSignature === next.separatingSoundTrackIdsSignature &&
  previous.mergingTrackIdsSignature === next.mergingTrackIdsSignature &&
  previous.splitHoverTrackId === next.splitHoverTrackId &&
  previous.splitPreviewTrackId === next.splitPreviewTrackId &&
  previous.splitPreviewMs === next.splitPreviewMs &&
  previous.splitScissorPulseFrame === next.splitScissorPulseFrame
);

const areTimelineLaneLayoutPresentationsEqual = (
  previous: TimelineLaneLayoutPresentation,
  next: TimelineLaneLayoutPresentation
): boolean => previous.laneLeftColumnWidthPx === next.laneLeftColumnWidthPx;

const areTimelineLaneRowPropsEqual = (
  previous: WorkspaceTimelineLaneRowProps,
  next: WorkspaceTimelineLaneRowProps
): boolean => (
  areTimelineLanePresentationRowsEqual(previous.row, next.row) &&
  areTimelineRangePresentationsEqual(previous.range, next.range) &&
  areTimelineLaneLayoutPresentationsEqual(previous.layout, next.layout) &&
  areTimelineToolStatesEqual(previous.tools, next.tools) &&
  previous.actions === next.actions
);

export const WorkspaceTimelineLaneRow = React.memo(({
  row,
  range,
  layout,
  tools,
  actions,
}: WorkspaceTimelineLaneRowProps) => {
  const { laneLeftColumnWidthPx } = layout;
  const {
    isSplitToolActive,
    isSoundSeparationToolActive,
    isSoundVolumeToolActive,
    canReorderTrackOrder,
  } = tools;
  const {
    onToggleTrackVisibility,
    onRegisterLaneRef,
    onStartTrackOrderDrag,
    onStartTrackDrag,
    onTrackTimelineToolMouseDown,
    onTrackTimelineToolContextMenu,
    onResolveTrackFromLanePointer,
    onResolveSplitMsFromLanePointer,
    onSetSplitHoverTrackId,
    onSetSplitPreview,
  } = actions;
  const {
    primaryTrack,
    laneTracks,
    timelineDropPreviewOffsetPercent,
    timelineDropPreviewWidthPercent,
    timelineDropPreviewColor,
    isTrackLaneCompactPreviewTrack,
    trackLanePreviewOffsetPercent,
    trackLanePreviewWidthPercent,
    hasTrackLanePreviewConflict,
    activeTrackId,
    activeDragTrackId,
    activeTrackOrderDragId,
    selectedLayerIdsSignature,
    separatingSoundTrackIdsSignature,
    mergingTrackIdsSignature,
    splitHoverTrackId,
    splitPreviewTrackId,
    splitPreviewMs,
    splitScissorPulseFrame,
  } = row;
  const trackDisplayName = primaryTrack.name || 'track';
  const isTimelineOrderDragActive = activeTrackOrderDragId === primaryTrack.layerId;
  const splitToolCursor = useMemo(() => (
    isSplitToolActive &&
    splitHoverTrackId &&
    splitScissorPulseFrame
  ) ? scissorsClosedCursor : scissorsCursor, [isSplitToolActive, splitHoverTrackId, splitScissorPulseFrame]);
  const rowStyle = useMemo<React.CSSProperties>(() => ({
    gridTemplateColumns: `${laneLeftColumnWidthPx}px minmax(0,1fr)`,
  }), [laneLeftColumnWidthPx]);
  const isPrimaryTrackSelected = signatureHasId(selectedLayerIdsSignature, primaryTrack.layerId);
  const headerStyle = useMemo<React.CSSProperties>(() => ({
    userSelect: 'none',
    color: !primaryTrack.isLayerVisible
      ? '#6B6B6B'
      : (isPrimaryTrackSelected || isTimelineOrderDragActive ? '#ECECEC' : '#AFAFAF'),
    fontSize: 13,
  }), [isPrimaryTrackSelected, isTimelineOrderDragActive, primaryTrack.isLayerVisible]);
  const reorderButtonStyle = useMemo<React.CSSProperties>(() => ({
    width: '20px',
    height: '28px',
    cursor: canReorderTrackOrder
      ? (isTimelineOrderDragActive ? 'grabbing' : 'grab')
      : 'default',
  }), [canReorderTrackOrder, isTimelineOrderDragActive]);
  const visibilityButtonStyle = useMemo<React.CSSProperties>(() => ({
    color: primaryTrack.isLayerVisible ? '#B4B4B4' : '#8E8E8E',
  }), [primaryTrack.isLayerVisible]);
  const laneStyle = useMemo<React.CSSProperties>(() => ({
    cursor: isSplitToolActive
      ? splitToolCursor
      : (isSoundSeparationToolActive
        ? 'pointer'
        : (isSoundVolumeToolActive ? 'ns-resize' : 'default')),
  }), [isSoundSeparationToolActive, isSoundVolumeToolActive, isSplitToolActive, splitToolCursor]);
  const timelineDropPreviewStyle = useMemo<React.CSSProperties | null>(() => {
    if (timelineDropPreviewOffsetPercent === null || timelineDropPreviewWidthPercent === null) return null;
    return {
      ...TIMELINE_LANE_DROP_PREVIEW_BASE_STYLE,
      left: `${timelineDropPreviewOffsetPercent}%`,
      width: `${timelineDropPreviewWidthPercent}%`,
      backgroundColor: timelineDropPreviewColor ?? undefined,
    };
  }, [timelineDropPreviewColor, timelineDropPreviewOffsetPercent, timelineDropPreviewWidthPercent]);
  const compactPreviewOverlayStyle = useMemo<React.CSSProperties>(() => ({
    ...TIMELINE_LANE_COMPACT_PREVIEW_OVERLAY_BASE_STYLE,
    border: `1px dashed ${hasTrackLanePreviewConflict ? 'rgba(242,139,130,0.8)' : 'rgba(236,236,236,0.35)'}`,
    backgroundColor: hasTrackLanePreviewConflict
      ? 'rgba(242,139,130,0.08)'
      : 'rgba(255,255,255,0.04)',
  }), [hasTrackLanePreviewConflict]);
  const compactPreviewBlockStyle = useMemo<React.CSSProperties | null>(() => {
    if (trackLanePreviewOffsetPercent === null || trackLanePreviewWidthPercent === null) return null;
    return {
      ...TIMELINE_LANE_COMPACT_PREVIEW_BLOCK_BASE_STYLE,
      left: `${trackLanePreviewOffsetPercent}%`,
      width: `${trackLanePreviewWidthPercent}%`,
      border: `1px dashed ${hasTrackLanePreviewConflict ? 'rgba(242,139,130,0.9)' : 'rgba(236,236,236,0.55)'}`,
      backgroundColor: hasTrackLanePreviewConflict
        ? 'rgba(242,139,130,0.25)'
        : 'rgba(236,236,236,0.18)',
    };
  }, [hasTrackLanePreviewConflict, trackLanePreviewOffsetPercent, trackLanePreviewWidthPercent]);
  const handleHeaderContextMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    onTrackTimelineToolContextMenu(event, primaryTrack);
  }, [onTrackTimelineToolContextMenu, primaryTrack]);
  const handleOrderMouseDown = useCallback((event: React.MouseEvent<HTMLElement>) => {
    onStartTrackOrderDrag(primaryTrack, event);
  }, [onStartTrackOrderDrag, primaryTrack]);
  const handleOrderClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);
  const handleVisibilityMouseDown = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  }, []);
  const handleVisibilityClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onToggleTrackVisibility(primaryTrack.layerId);
  }, [onToggleTrackVisibility, primaryTrack.layerId]);
  const handleLaneRef = useCallback((element: HTMLDivElement | null) => {
    onRegisterLaneRef(laneTracks, element);
  }, [laneTracks, onRegisterLaneRef]);
  const handleLaneMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!(isSplitToolActive || isSoundSeparationToolActive || isSoundVolumeToolActive)) return;
    const targetTrack = onResolveTrackFromLanePointer(laneTracks, event.currentTarget, event.clientX);
    if (!targetTrack) return;
    onTrackTimelineToolMouseDown(event, targetTrack);
  }, [isSoundSeparationToolActive, isSoundVolumeToolActive, isSplitToolActive, laneTracks, onResolveTrackFromLanePointer, onTrackTimelineToolMouseDown]);
  const handleLaneContextMenu = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const targetTrack = onResolveTrackFromLanePointer(laneTracks, event.currentTarget, event.clientX) ?? primaryTrack;
    onTrackTimelineToolContextMenu(event, targetTrack);
  }, [laneTracks, onResolveTrackFromLanePointer, onTrackTimelineToolContextMenu, primaryTrack]);
  const handleLaneMouseEnter = useCallback(() => {
    if (!isSplitToolActive) return;
    onSetSplitHoverTrackId((current) => current ?? primaryTrack.layerId);
  }, [isSplitToolActive, onSetSplitHoverTrackId, primaryTrack.layerId]);
  const handleLaneMouseMove = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!isSplitToolActive) return;
    const targetTrack = onResolveTrackFromLanePointer(laneTracks, event.currentTarget, event.clientX);
    if (!targetTrack) return;
    const splitMs = onResolveSplitMsFromLanePointer(targetTrack, event.currentTarget, event.clientX);
    if (splitMs === null) return;
    onSetSplitHoverTrackId(targetTrack.layerId);
    onSetSplitPreview((current) => {
      if (
        current &&
        current.trackId === targetTrack.layerId &&
        Math.abs(current.splitMs - splitMs) < 1
      ) {
        return current;
      }
      return { trackId: targetTrack.layerId, splitMs };
    });
  }, [isSplitToolActive, laneTracks, onResolveSplitMsFromLanePointer, onResolveTrackFromLanePointer, onSetSplitHoverTrackId, onSetSplitPreview]);
  const handleLaneMouseLeave = useCallback(() => {
    onSetSplitHoverTrackId((current) => (
      current && laneTracks.some((laneTrack) => laneTrack.layerId === current) ? null : current
    ));
    onSetSplitPreview((current) => (
      current && laneTracks.some((laneTrack) => laneTrack.layerId === current.trackId) ? null : current
    ));
  }, [laneTracks, onSetSplitHoverTrackId, onSetSplitPreview]);
  const trackBlockActions = useMemo<TimelineTrackBlockActions>(() => ({
    onStartTrackDrag,
    onTrackContextMenu: onTrackTimelineToolContextMenu,
  }), [onStartTrackDrag, onTrackTimelineToolContextMenu]);
  const trackBlockStatesByLayerId = useMemo<Record<string, TimelineTrackBlockState>>(() => {
    const statesByLayerId: Record<string, TimelineTrackBlockState> = {};
    laneTracks.forEach((laneTrack) => {
      statesByLayerId[laneTrack.layerId] = {
        splitPreviewMs: splitPreviewTrackId === laneTrack.layerId ? splitPreviewMs : null,
        isActive: activeTrackId === laneTrack.layerId,
        isDragging: activeDragTrackId === laneTrack.layerId,
        isSelected: signatureHasId(selectedLayerIdsSignature, laneTrack.layerId),
        isSeparatingSoundTrack: signatureHasId(separatingSoundTrackIdsSignature, laneTrack.layerId),
        isMergingTrack: signatureHasId(mergingTrackIdsSignature, laneTrack.layerId),
        splitToolCursor,
      };
    });
    return statesByLayerId;
  }, [
    activeDragTrackId,
    activeTrackId,
    laneTracks,
    mergingTrackIdsSignature,
    selectedLayerIdsSignature,
    separatingSoundTrackIdsSignature,
    splitPreviewMs,
    splitPreviewTrackId,
    splitToolCursor,
  ]);

  return (
    <div
      data-track-row="true"
      className="group grid w-full items-center gap-2"
      style={rowStyle}
    >
      <div
        className="flex min-w-0 items-center gap-1"
        title={trackDisplayName}
        onContextMenu={handleHeaderContextMenu}
        style={headerStyle}
      >
        <button
          type="button"
          title={canReorderTrackOrder ? 'Drag to reorder track' : 'Track reorder unavailable'}
          aria-label={`Drag to reorder ${trackDisplayName}`}
          aria-disabled={!canReorderTrackOrder}
          onMouseDown={handleOrderMouseDown}
          onClick={handleOrderClick}
          className={`inline-flex shrink-0 items-center justify-center rounded-full text-[#8E8E8E] transition-opacity ${
            canReorderTrackOrder && !isTimelineOrderDragActive ? 'opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100' : ''
          } ${canReorderTrackOrder ? '' : 'invisible'}`}
          style={reorderButtonStyle}
        >
          <GripVertical size={14} strokeWidth={2} />
        </button>
        <span className="min-w-0 flex-1 truncate" style={TIMELINE_LANE_NAME_STYLE}>{trackDisplayName}</span>
        {laneTracks.length > 1 ? (
          <div
            title="Uncompact a track before hiding it."
            aria-label={`Compacted lane with ${laneTracks.length} tracks. Uncompact a track before hiding it.`}
            style={TIMELINE_LANE_COMPACT_COUNT_STYLE}
          >
            {laneTracks.length}
          </div>
        ) : (
          <button
            type="button"
            title={primaryTrack.isLayerVisible ? 'Hide track' : 'Show track'}
            aria-label={primaryTrack.isLayerVisible ? 'Hide track' : 'Show track'}
            onMouseDown={handleVisibilityMouseDown}
            onClick={handleVisibilityClick}
            className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-opacity hover:bg-white/[0.08] ${
              primaryTrack.isLayerVisible ? 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100' : ''
            }`}
            style={visibilityButtonStyle}
          >
            {primaryTrack.isLayerVisible
              ? <Eye size={16} strokeWidth={2} />
              : <EyeOff size={16} strokeWidth={2} />}
          </button>
        )}
      </div>
      <div
        data-track-lane="true"
        ref={handleLaneRef}
        className="relative h-7 flex-1 overflow-hidden"
        onMouseDown={handleLaneMouseDown}
        onContextMenu={handleLaneContextMenu}
        onMouseEnter={handleLaneMouseEnter}
        onMouseMove={handleLaneMouseMove}
        onMouseLeave={handleLaneMouseLeave}
        style={laneStyle}
      >
        {timelineDropPreviewStyle !== null && (
          <div
            className="pointer-events-none"
            style={timelineDropPreviewStyle}
            title="Drop to add track here"
          />
        )}
        {isTrackLaneCompactPreviewTrack && compactPreviewBlockStyle !== null && (
          <>
            <div className="pointer-events-none" style={compactPreviewOverlayStyle} />
            <div className="pointer-events-none" style={compactPreviewBlockStyle} />
          </>
        )}
        {laneTracks.map((laneTrack) => {
          const trackBlockState = trackBlockStatesByLayerId[laneTrack.layerId];
          if (!trackBlockState) return null;
          return (
            <WorkspaceTimelineTrackBlock
              key={laneTrack.layerId}
              track={laneTrack}
              range={range}
              state={trackBlockState}
              tools={tools}
              actions={trackBlockActions}
            />
          );
        })}
      </div>
    </div>
  );
}, areTimelineLaneRowPropsEqual);

WorkspaceTimelineLaneRow.displayName = 'WorkspaceTimelineLaneRow';
